import { useCallback, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const MAX_BATCH_SIZE = 80;

const useBatchDataLoader = (load: (ids: string[]) => Promise<unknown>) => {
  const pendingIdsRef = useRef(new Set<string>());
  const handledIdsRef = useRef(new Set<string>());

  const debouncedLoadAllPending = useDebouncedCallback(
    () => {
      if (pendingIdsRef.current.size === 0) return;
      const pendingIds = [...pendingIdsRef.current];
      const batchedIds = pendingIds.splice(0, MAX_BATCH_SIZE);
      const restIds = pendingIds;
      load(batchedIds);
      batchedIds.forEach((id) => handledIdsRef.current.add(id));
      pendingIdsRef.current = new Set(restIds);
      if (pendingIdsRef.current.size > 0) {
        debouncedLoadAllPending();
      }
    },
    100,
    { maxWait: 100 },
  );

  const schedule = useCallback(
    (id: string) => {
      if (!handledIdsRef.current.has(id)) {
        pendingIdsRef.current.add(id);
        debouncedLoadAllPending();
      }
    },
    [debouncedLoadAllPending],
  );

  return schedule;
};

export default useBatchDataLoader;
